import { apiFetch } from "@/api/client";
import type {
  ActiveFolderResponse,
  AnalysisTemplateResponse,
  AnalysisTemplateUpsert,
  ApiKeyResponse,
  ApiKeyUpsert,
  ClientCreate,
  ClientResponse,
  ClientSummary,
  ClientUpdate,
  CustomFieldEntityType,
  CustomFieldSchemaCreate,
  CustomFieldSchemaResponse,
  CustomFieldSchemaUpdate,
  EmployeeCreate,
  EmployeeResponse,
  EmployeeSummary,
  EmployeeUpdate,
  ErrorListItem,
  Folder,
  MeetingCard as MeetingCardDto,
  FolderCreate,
  FolderMember,
  FolderMemberCreate,
  FolderUpdate,
  InviteCreate,
  InviteLinkResponse,
  PasswordResetLinkResponse,
  PasswordResetResponse,
  InviteResponse,
  ListAnalysisCreate,
  ListAnalysisMode,
  ListAnalysisResponse,
  ListAnalysisSummary,
  ManagerAnalysisCreatePayload,
  ManagerAnalysisResponse,
  ManagerAnalysisSummary,
  MeetingTypeCreate,
  MeetingTypeResponse,
  MeetingTypeSummary,
  MeetingTypeUpdate,
  PromptVersionCreate,
  PromptVersionResponse,
  SystemSettingResponse,
  SystemSettingUpsert,
  SystemSettings,
  UUID,
  UserFolderMembershipCreate,
  UserFolderMembershipResponse,
  UserListItem,
  UserUpdate,
} from "@/api/types";

// ---- Meeting types ------------------------------------------------------

/** GET /api/meeting_types — list of selectable types for the upload form. */
export function listMeetingTypes(): Promise<MeetingTypeSummary[]> {
  return apiFetch<MeetingTypeSummary[]>("/api/meeting_types", {
    method: "GET",
  });
}

/** GET /api/meeting_types — full DTOs for admin pages. */
export function listMeetingTypesFull(): Promise<MeetingTypeResponse[]> {
  return apiFetch<MeetingTypeResponse[]>("/api/meeting_types", {
    method: "GET",
  });
}

export function getMeetingType(id: UUID): Promise<MeetingTypeResponse> {
  return apiFetch<MeetingTypeResponse>(`/api/meeting_types/${id}`, {
    method: "GET",
  });
}

export function createMeetingType(
  payload: MeetingTypeCreate,
): Promise<MeetingTypeResponse> {
  return apiFetch<MeetingTypeResponse>("/api/meeting_types", {
    method: "POST",
    body: payload,
  });
}

export function updateMeetingType(
  id: UUID,
  payload: MeetingTypeUpdate,
): Promise<MeetingTypeResponse> {
  return apiFetch<MeetingTypeResponse>(`/api/meeting_types/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

// ---- Clients ------------------------------------------------------------

export interface ClientListQuery {
  search?: string;
  custom_field_key?: string;
  custom_field_value?: string;
  limit?: number;
  offset?: number;
}

function buildQuery(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s.length === 0 ? "" : `?${s}`;
}

/**
 * GET /api/clients — list of all clients (RBAC view-only enforced on backend).
 *
 * Papki-tenants v1: opt-in `folderId` narrows the result to one folder. The
 * default (omitted / `null`) is backward-compatible — used by inline-select
 * pickers (`ParticipantsTab`, upload form) that want a global lookup, not
 * the registry scope.
 */
export function listClients(
  folderId: UUID | null = null,
): Promise<ClientSummary[]> {
  return apiFetch<ClientSummary[]>(
    `/api/clients${buildQuery({ folder_id: folderId ?? undefined })}`,
    { method: "GET" },
  );
}

/**
 * GET /api/clients — full DTOs with filters for the registry view.
 *
 * Papki-tenants v1: opt-in `folderId` is appended to the query-string so
 * the backend's resolution chain (`query.folder_id` → `X-Active-Folder`
 * → session) sees it. Defaults to `null` for backward compatibility with
 * inline-form callers (`ClientFormDialog`, `UploadStepMetadata`).
 */
export function listClientsFull(
  q: ClientListQuery = {},
  folderId: UUID | null = null,
): Promise<ClientResponse[]> {
  const params = folderId !== null ? { ...q, folder_id: folderId } : q;
  return apiFetch<ClientResponse[]>(`/api/clients${buildQuery(params)}`, {
    method: "GET",
  });
}

export interface CreateClientPayload {
  name: string;
  aliases?: string[];
}

/** POST /api/clients — inline create from the upload form. */
export function createClient(
  payload: CreateClientPayload | ClientCreate,
): Promise<ClientSummary> {
  return apiFetch<ClientSummary>("/api/clients", {
    method: "POST",
    body: payload,
  });
}

/** GET /api/clients/{id} — full DTO for the client card (TM-035ae774). */
export function getClient(id: UUID): Promise<ClientResponse> {
  return apiFetch<ClientResponse>(`/api/clients/${id}`, { method: "GET" });
}

export function updateClient(
  id: UUID,
  payload: ClientUpdate,
): Promise<ClientResponse> {
  return apiFetch<ClientResponse>(`/api/clients/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

// ---- Employees ----------------------------------------------------------

export interface EmployeeListQuery {
  search?: string;
  custom_field_key?: string;
  custom_field_value?: string;
  limit?: number;
  offset?: number;
  /**
   * TM-5d6213f1 — when omitted/false, BE returns ONLY `is_active=true`
   * rows. Pass `true` to include inactive (soft-deleted) employees in the
   * response so the registry can render an archive view.
   */
  include_inactive?: boolean;
}

/**
 * GET /api/employees — list of all employees for participants picker.
 *
 * Papki-tenants v1: opt-in `folderId` narrows to one folder. Default keeps
 * the legacy global-lookup behaviour for `ParticipantsTab` /
 * `FolderEmployeesPanel` callers.
 */
export function listEmployees(
  folderId: UUID | null = null,
): Promise<EmployeeSummary[]> {
  return apiFetch<EmployeeSummary[]>(
    `/api/employees${buildQuery({ folder_id: folderId ?? undefined })}`,
    { method: "GET" },
  );
}

/**
 * GET /api/employees — full DTOs for the registry.
 *
 * Papki-tenants v1: opt-in `folderId` is appended to the query-string.
 * Defaults to `null` so inline-form pickers (`EmployeeFormDialog`,
 * `InviteFormDialog`, `UploadStepMetadata`) keep the global lookup.
 */
export function listEmployeesFull(
  q: EmployeeListQuery = {},
  folderId: UUID | null = null,
): Promise<EmployeeResponse[]> {
  const params = folderId !== null ? { ...q, folder_id: folderId } : q;
  return apiFetch<EmployeeResponse[]>(`/api/employees${buildQuery(params)}`, {
    method: "GET",
  });
}

/** GET /api/employees/{id} — full DTO for the employee card (TM-035ae774). */
export function getEmployee(id: UUID): Promise<EmployeeResponse> {
  return apiFetch<EmployeeResponse>(`/api/employees/${id}`, { method: "GET" });
}

export function createEmployee(
  payload: EmployeeCreate,
): Promise<EmployeeResponse> {
  return apiFetch<EmployeeResponse>("/api/employees", {
    method: "POST",
    body: payload,
  });
}

export function updateEmployee(
  id: UUID,
  payload: EmployeeUpdate,
): Promise<EmployeeResponse> {
  return apiFetch<EmployeeResponse>(`/api/employees/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

/**
 * POST /api/employees/{id}/deactivate — admin only (TM-5d6213f1).
 *
 * Soft-deletes the employee by flipping `is_active=false`. Idempotent:
 * calling on an already-inactive employee returns 200 with the same row.
 * Hard delete is forbidden (ADR-011 — orphan FKs on participants/matching).
 */
export function deactivateEmployee(id: UUID): Promise<EmployeeResponse> {
  return apiFetch<EmployeeResponse>(`/api/employees/${id}/deactivate`, {
    method: "POST",
  });
}

/**
 * POST /api/employees/{id}/restore — admin only (TM-5d6213f1). Mirror of
 * `deactivateEmployee`: sets `is_active=true`, idempotent.
 */
export function restoreEmployee(id: UUID): Promise<EmployeeResponse> {
  return apiFetch<EmployeeResponse>(`/api/employees/${id}/restore`, {
    method: "POST",
  });
}

// ---- Settings (upload widget reads a flat shape; full registry is JSON) ---

/** GET /api/settings — UI uses upload limits & allowed extensions. */
export function getSystemSettings(): Promise<SystemSettings> {
  return apiFetch<SystemSettings>("/api/settings", { method: "GET" });
}

export function listSystemSettings(): Promise<SystemSettingResponse[]> {
  return apiFetch<SystemSettingResponse[]>("/api/settings", { method: "GET" });
}

export function updateSystemSetting(
  payload: SystemSettingUpsert,
): Promise<SystemSettingResponse> {
  return apiFetch<SystemSettingResponse>("/api/settings", {
    method: "PUT",
    body: payload,
  });
}

// ---- Prompts ------------------------------------------------------------

export function listPrompts(
  meetingTypeId: UUID,
): Promise<PromptVersionResponse[]> {
  return apiFetch<PromptVersionResponse[]>(
    `/api/prompts?meeting_type_id=${meetingTypeId}`,
    { method: "GET" },
  );
}

export function createPromptVersion(
  payload: PromptVersionCreate,
): Promise<PromptVersionResponse> {
  return apiFetch<PromptVersionResponse>("/api/prompts", {
    method: "POST",
    body: payload,
  });
}

/**
 * Activate an existing prompt version: backend models this as a *new* POST
 * mirroring the same text + activate=true. The API has no PATCH for prompts
 * because every change should produce a new immutable version (ADR-010).
 */
export function activatePromptVersion(
  promptVersion: PromptVersionResponse,
): Promise<PromptVersionResponse> {
  return createPromptVersion({
    meeting_type_id: promptVersion.meeting_type_id,
    text: promptVersion.text,
    activate: true,
  });
}

// ---- API keys (never round-trips the secret value) ----------------------

export function listApiKeys(): Promise<ApiKeyResponse[]> {
  return apiFetch<ApiKeyResponse[]>("/api/api_keys", { method: "GET" });
}

export function upsertApiKey(payload: ApiKeyUpsert): Promise<ApiKeyResponse> {
  return apiFetch<ApiKeyResponse>("/api/api_keys", {
    method: "PUT",
    body: payload,
  });
}

// ---- Errors -------------------------------------------------------------

export interface ErrorListQuery {
  phase?: number;
  provider?: string;
  since?: string;
  until?: string;
  meeting_id?: UUID;
  limit?: number;
  offset?: number;
}

export function listErrors(q: ErrorListQuery = {}): Promise<ErrorListItem[]> {
  return apiFetch<ErrorListItem[]>(`/api/errors${buildQuery(q)}`, {
    method: "GET",
  });
}

// ---- Custom field schemas ----------------------------------------------

export function listCustomFieldSchemas(
  entityType: CustomFieldEntityType,
): Promise<CustomFieldSchemaResponse[]> {
  return apiFetch<CustomFieldSchemaResponse[]>(
    `/api/custom_fields?entity_type=${entityType}`,
    { method: "GET" },
  );
}

export function createCustomFieldSchema(
  payload: CustomFieldSchemaCreate,
): Promise<CustomFieldSchemaResponse> {
  return apiFetch<CustomFieldSchemaResponse>("/api/custom_fields", {
    method: "POST",
    body: payload,
  });
}

export function updateCustomFieldSchema(
  id: UUID,
  payload: CustomFieldSchemaUpdate,
): Promise<CustomFieldSchemaResponse> {
  return apiFetch<CustomFieldSchemaResponse>(`/api/custom_fields/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

// ---- Users --------------------------------------------------------------

export function listUsers(): Promise<UserListItem[]> {
  return apiFetch<UserListItem[]>("/api/users", { method: "GET" });
}

export function updateUser(
  id: UUID,
  payload: UserUpdate,
): Promise<UserListItem> {
  return apiFetch<UserListItem>(`/api/users/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

// ---- User folder memberships (folder-membership v2 §2, Variant A) -------
//
// User-side mirror of `/api/folders/{folder_id}/members/*` — same
// surface, opposite direction. Contract:
// `docs/api/folder_membership_v2.contract.md` §2. Variant A retired the
// PATCH endpoint and the per-folder role; the surface is now POST +
// DELETE. Errors propagate the standard `RecognitionErrorEnvelope`
// (`code in {forbidden, not_found, conflict, validation_error}`) via
// `apiFetch` → `ApiError`.

/** POST /api/users/{user_id}/folders — §2.1. */
export function addUserToFolder(
  userId: UUID,
  body: UserFolderMembershipCreate,
): Promise<UserFolderMembershipResponse> {
  return apiFetch<UserFolderMembershipResponse>(
    `/api/users/${userId}/folders`,
    { method: "POST", body },
  );
}

/** DELETE /api/users/{user_id}/folders/{folder_id} — §2.3. Returns 204. */
export function removeUserFromFolder(
  userId: UUID,
  folderId: UUID,
): Promise<void> {
  return apiFetch<void>(`/api/users/${userId}/folders/${folderId}`, {
    method: "DELETE",
  });
}

// ---- Invites ------------------------------------------------------------

export function createInvite(payload: InviteCreate): Promise<InviteResponse> {
  return apiFetch<InviteResponse>("/api/invites", {
    method: "POST",
    body: payload,
  });
}

/**
 * T4: recover the copy-paste accept-invite URL for a pending invite. Admin
 * only — the token is never exposed in the bulk users list, so this
 * dedicated lookup is the safe way to surface "copy invite link again"
 * from the users registry.
 */
export function getInviteLink(id: UUID): Promise<InviteLinkResponse> {
  return apiFetch<InviteLinkResponse>(`/api/invites/${id}/link`, {
    method: "GET",
  });
}

// ---- Сброс пароля -------------------------------------------------------

/**
 * Выдать сброс пароля. Только администратор.
 *
 * Прежний пароль перестаёт работать в этот момент, а не когда человек
 * откроет ссылку: сброс запрашивают, когда доступом мог завладеть кто-то
 * ещё. Ссылку эта ручка не возвращает — за ней отдельный вызов, чтобы
 * «выдал» и «показал живой токен» остались разными событиями в аудите.
 */
export function createPasswordReset(
  userId: UUID,
): Promise<PasswordResetResponse> {
  return apiFetch<PasswordResetResponse>(
    `/api/users/${userId}/password-reset`,
    {
      method: "POST",
    },
  );
}

/** Показать ссылку ещё раз — если администратор закрыл окно. */
export function getPasswordResetLink(
  userId: UUID,
  resetId: UUID,
): Promise<PasswordResetLinkResponse> {
  return apiFetch<PasswordResetLinkResponse>(
    `/api/users/${userId}/password-reset/${resetId}/link`,
    { method: "GET" },
  );
}

// ---- Meetings registry --------------------------------------------------

export interface MeetingsListQuery {
  client_id?: UUID;
  meeting_type_id?: UUID;
  /** Employee.id — narrows to meetings where the employee is in participants[]. */
  participant_id?: UUID;
  status?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
  /**
   * Active folder (papki-tenants v1) — narrows the registry to one folder
   * even when the request header is stripped by an intermediate proxy.
   * Optional; when unset the backend falls back to the session's active
   * folder picked up from `X-Active-Folder`.
   */
  folder_id?: UUID;
  /**
   * Server-side sort (TM-035ae774). Whitelist on the backend:
   * {created_at, meeting_no, status}. Omitted ⇒ backend default
   * `created_at desc`. The backend always appends a `meeting_no desc`
   * tie-breaker for stable offset-pagination.
   */
  sort?: string;
  order?: "asc" | "desc";
}

/**
 * Serialise a meetings query into a `?...` string. `sort`/`order` are
 * appended only when set (TM-035ae774) — `buildQuery` skips
 * undefined/null/"" so the default registry call stays param-free and
 * back-compatible with the backend's `created_at desc` default.
 */
export function listMeetingsQS(q: MeetingsListQuery = {}): string {
  return buildQuery(q);
}

// ---- Folders (papki-tenants v1) -----------------------------------------

/** GET /api/folders — folders visible to the current user. */
export function listFolders(): Promise<Folder[]> {
  return apiFetch<Folder[]>("/api/folders", { method: "GET" });
}

/** GET /api/folders/active — the current session's active folder. */
export function getActiveFolder(): Promise<ActiveFolderResponse> {
  return apiFetch<ActiveFolderResponse>("/api/folders/active", {
    method: "GET",
  });
}

/** POST /api/folders/active — change the session's active folder. */
export function setActiveFolderApi(
  folderId: UUID,
): Promise<ActiveFolderResponse> {
  return apiFetch<ActiveFolderResponse>("/api/folders/active", {
    method: "POST",
    body: { folder_id: folderId },
  });
}

/** POST /api/folders — admin-only create. */
export function createFolder(payload: FolderCreate): Promise<Folder> {
  return apiFetch<Folder>("/api/folders", {
    method: "POST",
    body: payload,
  });
}

/** PATCH /api/folders/{id} — admin-only. Меняет настройки папки. */
export function updateFolder(id: UUID, payload: FolderUpdate): Promise<Folder> {
  return apiFetch<Folder>(`/api/folders/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

// ---- Folder members (admin-only management) -----------------------------

/** GET /api/folders/{id}/members — visible to admin or folder members. */
export function listFolderMembers(folderId: UUID): Promise<FolderMember[]> {
  return apiFetch<FolderMember[]>(`/api/folders/${folderId}/members`, {
    method: "GET",
  });
}

/** POST /api/folders/{id}/members — admin only (Variant A, no role). */
export function addFolderMember(
  folderId: UUID,
  payload: FolderMemberCreate,
): Promise<FolderMember> {
  return apiFetch<FolderMember>(`/api/folders/${folderId}/members`, {
    method: "POST",
    body: payload,
  });
}

/**
 * POST /api/folders/{id}/members/{user_id}/remove — admin only.
 *
 * Modelled as POST rather than DELETE per ADR-011 (no destructive HTTP
 * verbs in the surface). The backend refuses to drain the bootstrap
 * **default** folder's last member with a 409 — the FE should surface
 * the server's message verbatim via `toMessage`.
 */
export function removeFolderMember(
  folderId: UUID,
  userId: UUID,
): Promise<void> {
  return apiFetch<void>(`/api/folders/${folderId}/members/${userId}/remove`, {
    method: "POST",
  });
}

// ---- Folder employees (M:N link table) -----------------------------------

/** POST /api/folders/{id}/employees — admin only. */
export function linkFolderEmployee(
  folderId: UUID,
  employeeId: UUID,
): Promise<{ folder_id: UUID; employee_id: UUID }> {
  return apiFetch<{ folder_id: UUID; employee_id: UUID }>(
    `/api/folders/${folderId}/employees`,
    {
      method: "POST",
      body: { employee_id: employeeId },
    },
  );
}

/**
 * POST /api/folders/{id}/employees/{employee_id}/unlink — admin only.
 * Mirror of `removeFolderMember` — POST per ADR-011.
 */
export function unlinkFolderEmployee(
  folderId: UUID,
  employeeId: UUID,
): Promise<void> {
  return apiFetch<void>(
    `/api/folders/${folderId}/employees/${employeeId}/unlink`,
    { method: "POST" },
  );
}

// ---- Move helpers (folder-management-v3 §3-§5) --------------------------
//
// Three per-resource «move» endpoints introduced in
// `docs/api/folder-management-v3.contract.md`. All three accept the same
// `{ folder_id }` body and return the full updated resource so the FE can
// swap the cached row without a follow-up GET. Errors propagate the
// standard `RecognitionErrorEnvelope` (`code in {forbidden, not_found,
// validation_error}`) via `apiFetch` → `ApiError`.

/**
 * POST /api/meetings/{meeting_id}/move — folder-management-v3 §3.
 * Returns the updated MeetingCard (folder_id is the new folder).
 *
 * Permission (§3.3): admin any-to-any; non-admin requires both source AND
 * target folders to be in `principal.folder_ids`. Source-not-member
 * returns 404 (anti-leak), target-not-member returns 403.
 */
export function moveMeeting(
  meetingId: UUID,
  folderId: UUID,
): Promise<MeetingCardDto> {
  return apiFetch<MeetingCardDto>(`/api/meetings/${meetingId}/move`, {
    method: "POST",
    body: { folder_id: folderId },
  });
}

// NOTE: `moveEmployee` was removed in TM-5d6213f1. Employee folder
// membership is now multi-folder; the FE mutates it via PATCH `folder_ids`
// (atomic replace) — see `updateEmployee`.

// ---- Manager Analysis (ADR-019) --------------------------------------------

/** GET /api/analysis_templates — список шаблонов запросов. Используется
 *  и как feature-gate: 404 → фича выключена флагом. */
export function listAnalysisTemplates(opts?: {
  includeInactive?: boolean;
}): Promise<AnalysisTemplateResponse[]> {
  const qs = opts?.includeInactive ? "?include_inactive=true" : "";
  return apiFetch<AnalysisTemplateResponse[]>(`/api/analysis_templates${qs}`, {
    method: "GET",
  });
}

export function createAnalysisTemplate(
  payload: AnalysisTemplateUpsert,
): Promise<AnalysisTemplateResponse> {
  return apiFetch<AnalysisTemplateResponse>("/api/analysis_templates", {
    method: "POST",
    body: payload,
  });
}

export function updateAnalysisTemplate(
  id: UUID,
  payload: AnalysisTemplateUpsert,
): Promise<AnalysisTemplateResponse> {
  return apiFetch<AnalysisTemplateResponse>(`/api/analysis_templates/${id}`, {
    method: "PUT",
    body: payload,
  });
}

export function createManagerAnalysis(
  employeeId: UUID,
  payload: ManagerAnalysisCreatePayload,
): Promise<ManagerAnalysisResponse> {
  return apiFetch<ManagerAnalysisResponse>(
    `/api/employees/${employeeId}/analyses`,
    { method: "POST", body: payload },
  );
}

export function listManagerAnalyses(
  employeeId: UUID,
  opts?: { limit?: number; offset?: number },
): Promise<ManagerAnalysisSummary[]> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return apiFetch<ManagerAnalysisSummary[]>(
    `/api/employees/${employeeId}/analyses${qs ? `?${qs}` : ""}`,
    { method: "GET" },
  );
}

export function getManagerAnalysis(
  analysisId: UUID,
): Promise<ManagerAnalysisResponse> {
  return apiFetch<ManagerAnalysisResponse>(
    `/api/manager_analyses/${analysisId}`,
    { method: "GET" },
  );
}

// ---- List Analysis (TM-4c1fb82b) ------------------------------------------
//
// «Анализ по списку встреч». Also acts as the feature-gate: a 404 from
// `listListAnalyses` means the backend router isn't mounted (feature off).

export function createListAnalysis(
  payload: ListAnalysisCreate,
): Promise<ListAnalysisResponse> {
  return apiFetch<ListAnalysisResponse>("/api/list_analyses", {
    method: "POST",
    body: payload,
  });
}

export function listListAnalyses(opts?: {
  mode?: ListAnalysisMode;
  employee_id?: UUID;
  client_id?: UUID;
  limit?: number;
  offset?: number;
}): Promise<ListAnalysisSummary[]> {
  const qs = buildQuery({
    mode: opts?.mode,
    employee_id: opts?.employee_id,
    client_id: opts?.client_id,
    limit: opts?.limit,
    offset: opts?.offset,
  });
  return apiFetch<ListAnalysisSummary[]>(`/api/list_analyses${qs}`, {
    method: "GET",
  });
}

export function getListAnalysis(id: UUID): Promise<ListAnalysisResponse> {
  return apiFetch<ListAnalysisResponse>(`/api/list_analyses/${id}`, {
    method: "GET",
  });
}

/**
 * POST /api/clients/{client_id}/move — folder-management-v3 §5.
 * Admin only. Returns the updated ClientResponse.
 *
 * Non-admin moves continue to flow through `updateClient` (PATCH body
 * with `folder_id`) — the v2 §3.2 member-to-member contract; that path
 * is unchanged. The `/move` endpoint is the admin-only parity surface.
 */
export function moveClient(
  clientId: UUID,
  folderId: UUID,
): Promise<ClientResponse> {
  return apiFetch<ClientResponse>(`/api/clients/${clientId}/move`, {
    method: "POST",
    body: { folder_id: folderId },
  });
}
